import type { ItemVariantAxisOptions } from '../api/masters';
import type { UnitCondition } from '../generated/enums';
import { suggestShortCode } from './garmentCode';
import { emptyLine, type IntakeDraft, type IntakeLine } from './intake';
import { maxPerSize } from './receive';
import { isValidReference, normalizeReference, referencesOf } from './referenceNumber';

/**
 * Importing stock from a shop's own spreadsheet (canvas "Stock import from Excel", owner 2026-10-23):
 * whatever layout the shop keeps, the person says which column is which and what its words mean,
 * sees every row's fate before anything is saved, and imports.
 *
 * <p>
 * <strong>Nothing here reads a file.</strong> The website opens the workbook (no endpoint accepts a
 * file) and hands this module a plain grid of text and, per row, an opaque picture key. What comes
 * out is the website's own intake drafts (`units/intake.ts`), sent through the same
 * `toBatchRequest` the New stock page and the phone use — so an imported garment is booked in
 * exactly as a typed one, codes, prices and checks included.
 * </p>
 */

/* ---------------------------------------------------------------- the sheet */

/** One row of the sheet as read: its number in the sheet (for the person), its cells as text. */
export interface SheetRow {
  /** The row's number as the spreadsheet shows it, so "row 41" means what the person sees. */
  number: number;
  cells: string[];
  /** A picture found on this row, as a key the reader understands; null when none. */
  picture: string | null;
}

export interface Sheet {
  headers: string[];
  rows: SheetRow[];
}

/** A column's letter as a spreadsheet shows it: 0 → A, 26 → AA. */
export function columnLetter(index: number): string {
  let letters = '';
  let rest = index + 1;

  while (rest > 0) {
    const digit = (rest - 1) % 26;
    letters = String.fromCharCode(65 + digit) + letters;
    rest = Math.floor((rest - 1) / 26);
  }

  return letters;
}

/**
 * Which of the first rows holds the headings: the one with the most words in it — a title row
 * ("Shreeji Stock Oct 2026") has one, a data row has numbers. Index from 0; 0 for an empty grid.
 */
export function guessHeaderRow(grid: readonly (readonly string[])[]): number {
  let best = 0;
  let bestScore = -1;

  grid.slice(0, 15).forEach((cells, index) => {
    const score = cells.filter((value) => {
      const text = value.trim();

      return text !== '' && !Number.isFinite(Number(text.replace(/,/g, '')));
    }).length;

    if (score > bestScore) {
      best = index;
      bestScore = score;
    }
  });

  return best;
}

/**
 * The sheet below a heading row: headings (a blank one named by its letter), then every row with
 * anything in it, numbered as the spreadsheet numbers them.
 */
export function sheetFrom(
  grid: readonly (readonly string[])[],
  headerRow: number,
  pictureAt: (rowIndex: number) => string | null = () => null,
): Sheet {
  const headingCells = grid[headerRow] ?? [];
  const width = Math.max(
    headingCells.length,
    ...grid.slice(headerRow + 1).map((cells) => cells.length),
    0,
  );
  const headers = Array.from({ length: width }, (_, index) => {
    const heading = (headingCells[index] ?? '').trim();

    return heading === '' ? `Column ${columnLetter(index)}` : heading;
  });
  const rows: SheetRow[] = [];

  grid.forEach((cells, index) => {
    if (index <= headerRow) {
      return;
    }

    const picture = pictureAt(index);
    const padded = headers.map((_, column) => (cells[column] ?? '').trim());

    if (padded.some((value) => value !== '') || picture !== null) {
      rows.push({ number: index + 1, cells: padded, picture });
    }
  });

  return { headers, rows };
}

/** A CSV file's text as a grid: quoted fields, doubled quotes, commas and newlines inside quotes. */
export function parseCsv(text: string): string[][] {
  const grid: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  const source = text.startsWith('﻿') ? text.slice(1) : text;

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]!;

    if (quoted) {
      if (char === '"' && source[index + 1] === '"') {
        field += '"';
        index += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        field += char;
      }
    } else if (char === '"' && field === '') {
      quoted = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && source[index + 1] === '\n') {
        index += 1;
      }

      row.push(field);
      grid.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    grid.push(row);
  }

  return grid;
}

/* ------------------------------------------------------------------ fields */

/** Where a column can go. An attribute (size, colour, fabric) is `attr:` and its code. */
export type FieldKey =
  | 'ignore'
  | 'designName'
  | 'designCode'
  | 'category'
  | 'quantity'
  | 'serial'
  | 'reference'
  | 'cost'
  | 'rentForEvent'
  | 'extraDayRent'
  | 'salePrice'
  | 'condition'
  | 'photo'
  | 'notes'
  | `attr:${string}`;

export const fieldLabels: Record<Exclude<FieldKey, `attr:${string}`>, string> = {
  ignore: 'Ignore this column',
  designName: 'Design name',
  designCode: 'Design code',
  category: 'Category',
  quantity: 'How many',
  serial: 'Garment code (own tags)',
  reference: 'Reference number (RN)',
  cost: 'Cost each',
  rentForEvent: 'Rent for the event',
  extraDayRent: 'Each extra day',
  salePrice: 'Sale price',
  condition: 'Condition',
  photo: 'Photo',
  notes: 'Notes',
};

/** The fixed fields in the order a person reads the dropdown; attributes follow, by name. */
export const fixedFields = Object.keys(fieldLabels) as Exclude<FieldKey, `attr:${string}`>[];

export function attributeField(code: string): FieldKey {
  return `attr:${code}`;
}

export function attributeCodeOf(field: FieldKey): string | null {
  return field.startsWith('attr:') ? field.slice(5) : null;
}

/** Headings shops actually use, after `headingKey` — English first, then the words a Gujarati shop writes. */
const synonyms: Record<Exclude<FieldKey, 'ignore' | `attr:${string}`>, readonly string[]> = {
  designName: [
    'itemname',
    'item',
    'design',
    'designname',
    'name',
    'product',
    'productname',
    'article',
    'particulars',
  ],
  designCode: [
    'code',
    'designcode',
    'itemcode',
    'sku',
    'styleno',
    'style',
    'articleno',
    'designno',
  ],
  category: ['category', 'type', 'group', 'itemtype', 'cat'],
  quantity: ['qty', 'quantity', 'pcs', 'pieces', 'nos', 'count', 'stock'],
  serial: ['serial', 'serialno', 'barcode', 'garmentcode'],
  reference: [
    'rn',
    'rnno',
    'ref',
    'refno',
    'reference',
    'referenceno',
    'referencenumber',
    'tag',
    'tagno',
    'tagnumber',
    'shopno',
    'oldno',
  ],
  cost: [
    'cost',
    'costeach',
    'purchase',
    'purchasers',
    'purchaseprice',
    'costprice',
    'buyprice',
    'pp',
  ],
  rentForEvent: ['rent', 'rate', 'rental', 'rentrate', 'hire', 'rentfortheevent', 'bhadu'],
  extraDayRent: ['extraday', 'eachextraday', 'perday', 'extra'],
  salePrice: ['saleprice', 'mrp', 'sellingprice', 'sp'],
  condition: ['condition'],
  photo: ['photo', 'image', 'picture', 'pic', 'img', 'photos'],
  notes: ['remark', 'remarks', 'note', 'notes', 'comment', 'comments'],
};

/** A heading as it is compared: lower case, letters and digits only ("Purchase Rs." → "purchasers"). */
export function headingKey(heading: string): string {
  return heading.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * The first guess at where each column goes, from its heading. Each field is taken once — the
 * first column that names it wins and a second "Name" column is left on ignore — and an attribute
 * is recognised by its own name or code ("Size", "Colour", "Color" → COLOUR).
 */
export function guessMapping(
  headers: readonly string[],
  attributes: readonly { code: string; name: string }[],
): FieldKey[] {
  const taken = new Set<FieldKey>();
  const guess = (heading: string): FieldKey => {
    const key = headingKey(heading);

    if (key === '') {
      return 'ignore';
    }

    for (const attribute of attributes) {
      const names = [headingKey(attribute.name), headingKey(attribute.code)];

      if (attribute.code.toUpperCase() === 'COLOUR' || headingKey(attribute.name) === 'colour') {
        names.push('color');
      }

      if (names.includes(key)) {
        return attributeField(attribute.code);
      }
    }

    for (const [field, words] of Object.entries(synonyms) as [FieldKey, readonly string[]][]) {
      if (words.includes(key)) {
        return field;
      }
    }

    return 'ignore';
  };

  return headers.map((heading) => {
    const field = guess(heading);

    if (field === 'ignore' || taken.has(field)) {
      return 'ignore';
    }

    taken.add(field);

    return field;
  });
}

/** Which columns are mapped to a field, or -1. */
function columnOf(mapping: readonly FieldKey[], field: FieldKey): number {
  return mapping.indexOf(field);
}

/**
 * What an import cannot do without, in words — "A design: its name or code" — or an empty list.
 * The axes a category needs are checked per row, since each row's category has its own.
 */
export function mappingProblems(mapping: readonly FieldKey[]): string[] {
  const problems: string[] = [];

  if (columnOf(mapping, 'designName') < 0 && columnOf(mapping, 'designCode') < 0) {
    problems.push('Choose the column with the design — its name or its code.');
  }

  if (
    columnOf(mapping, 'quantity') < 0 &&
    columnOf(mapping, 'serial') < 0 &&
    columnOf(mapping, 'reference') < 0
  ) {
    problems.push(
      'Choose how many came — a "How many" column, or each garment’s own tag or reference number.',
    );
  }

  return problems;
}

/* ------------------------------------------------------------------- words */

/** A word as it is compared: trimmed, lower case, inch and foot marks off, spaces collapsed. */
export function wordKey(word: string): string {
  return word
    .trim()
    .toLowerCase()
    .replace(/["'”“’]/g, '')
    .replace(/\s+/g, ' ');
}

/** What a word in the sheet was decided to mean. */
export type WordChoice = { kind: 'value'; id: number } | { kind: 'new' } | { kind: 'skip' };

/** Per field (the category, an attribute, the condition): each word key's choice. */
export type WordMap = Record<string, Record<string, WordChoice>>;

export interface Choosable {
  id: number;
  name: string;
  code: string;
}

/**
 * The known value a word already names — by name or code, ignoring case, inch marks and spaces, and
 * a number equal to a number ("40.0" is 40). Null when nothing matches, which is when the person is
 * asked.
 */
export function autoMatch(word: string, options: readonly Choosable[]): number | null {
  const key = wordKey(word);

  if (key === '') {
    return null;
  }

  const numeric = Number(key);

  for (const option of options) {
    if (wordKey(option.name) === key || wordKey(option.code) === key) {
      return option.id;
    }

    if (Number.isFinite(numeric) && key !== '' && Number(wordKey(option.name)) === numeric) {
      return option.id;
    }
  }

  return null;
}

/** The distinct words in a column, most used first, each with the rows that carry it. */
export function distinctWords(
  sheet: Sheet,
  column: number,
): { word: string; key: string; rows: number }[] {
  const counts = new Map<string, { word: string; rows: number }>();

  for (const row of sheet.rows) {
    const word = (row.cells[column] ?? '').trim();
    const key = wordKey(word);

    if (key === '') {
      continue;
    }

    const seen = counts.get(key);
    counts.set(key, { word: seen?.word ?? word, rows: (seen?.rows ?? 0) + 1 });
  }

  return [...counts.entries()]
    .map(([key, { word, rows }]) => ({ word, key, rows }))
    .sort((a, b) => b.rows - a.rows || a.word.localeCompare(b.word));
}

/** The conditions a sheet's words can name, with the words shops use for them. */
const conditionWords: Record<string, UnitCondition> = {
  new: 'New',
  fresh: 'New',
  excellent: 'Excellent',
  'very good': 'Excellent',
  good: 'Good',
  ok: 'Good',
  fair: 'Fair',
  used: 'Fair',
  poor: 'Poor',
  old: 'Poor',
  unusable: 'Unusable',
};

export function conditionOf(word: string): UnitCondition | null {
  return conditionWords[wordKey(word)] ?? null;
}

/* ------------------------------------------------------------- the numbers */

/** A money cell — "₹4,500", "Rs. 4500", "4500.00" — or null when blank; NaN when not a number. */
export function moneyOf(cell: string): number | null {
  const cleaned = cell.replace(/(rs\.?|inr|₹|,|\s)/gi, '');

  if (cleaned === '') {
    return null;
  }

  const value = Number(cleaned);

  return Number.isFinite(value) ? value : Number.NaN;
}

/** Own tag codes in one cell: comma, semicolon or line separated. */
export function serialsOf(cell: string): string[] {
  return cell
    .split(/[,;\n]+/)
    .map((serial) => serial.trim().toUpperCase())
    .filter((serial) => serial !== '');
}

/**
 * A row's fingerprint: its mapped content, plus how many identical rows came before it, hashed.
 * The same sheet imported again — or a fixed copy of it — finds the rows already in by this and
 * skips them; two genuinely identical rows stay two (their occurrence differs).
 */
export function fingerprintOf(content: string, occurrence: number): string {
  // FNV-1a, 64-bit in two 32-bit halves: stable across browsers and dependency-free.
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  const text = `${occurrence}|${content}`;

  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    h1 = Math.imul(h1 ^ code, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ code, 0x811c9dc5) >>> 0;
  }

  return `${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

/* ---------------------------------------------------------------- the plan */

export interface CatalogueDesign {
  id: number;
  code: string;
  name: string;
  categoryId: number;
  /** A retired design keeps its code taken but takes no stock; absent means active. */
  isActive?: boolean;
}

/** A design the import will create, keyed so rows can point at it before it has an id. */
export interface NewDesign {
  key: string;
  name: string;
  code: string;
  categoryId: number;
  /** A row's picture, offered as the design's first photo. */
  picture: string | null;
  /** The sale price (GST included) from its first row that gives one; null when none does. */
  salePrice: number | null;
}

/**
 * A size, colour or other value the person chose to add ("Add “XL” as a new size"). Created just
 * before the import; until then rows carrying it hold a placeholder id below zero.
 */
export interface NewValue {
  attributeId: number;
  attributeCode: string;
  attributeName: string;
  word: string;
  key: string;
  placeholderId: number;
}

export type RowStatus = 'ready' | 'warning' | 'out' | 'done';

export interface PlannedRow {
  number: number;
  status: RowStatus;
  /** Why it is a warning, left out or already done — the person's words, never a code. */
  reasons: string[];
  designName: string;
  /** The design it books into: an existing id, or a new design's key. */
  design: { id: number } | { key: string } | null;
  categoryId: number | null;
  axisValues: Record<number, number>;
  /** "Size 40 · Maroon" — the row's look, for the check screen. */
  look: string;
  quantity: number;
  serials: string[];
  /** The shop's own number for each garment, cleaned — "1-5" in the sheet is five of them. */
  references: string[];
  cost: number | null;
  rentForEvent: number | null;
  extraDayRent: number | null;
  condition: UnitCondition;
  picture: string | null;
  notes: string | null;
  fingerprint: string;
}

export interface ImportPlan {
  rows: PlannedRow[];
  newDesigns: NewDesign[];
  /** Values to add before importing; their rows hold `placeholderId` until then. */
  newValues: NewValue[];
  summary: {
    read: number;
    ready: number;
    warning: number;
    out: number;
    done: number;
    garments: number;
    ownTags: number;
    /** Garments that come in with a reference number (RN). */
    references: number;
    /** Photos that will be sent — one per new design that has one; known designs keep theirs. */
    pictures: number;
    cost: number;
  };
}

export interface PlanInput {
  sheet: Sheet;
  mapping: readonly FieldKey[];
  words: WordMap;
  designs: readonly CatalogueDesign[];
  categories: readonly Choosable[];
  /** Each category's axes, the ones a garment of it must carry; absent while still being read. */
  axesByCategory: Record<number, ItemVariantAxisOptions[] | undefined>;
  /** Attributes by code, to turn an `attr:` column into its attribute id. */
  attributeIds: Record<string, number>;
  /** Fingerprints of rows imported before — those rows are done, not booked twice. */
  imported: ReadonlySet<string>;
  /** Own tag codes already on a garment. */
  takenSerials: ReadonlySet<string>;
  /** Reference numbers already on a garment, cleaned. Absent reads as none. */
  takenReferences?: ReadonlySet<string> | undefined;
  /** The category for a row that names none — a sheet of one category often has no such column. */
  defaultCategoryId?: number | null | undefined;
  /** For a "file names" photo column: whether a chosen file goes by that name. */
  hasPhotoFile?: ((name: string) => boolean) | undefined;
}

/** Every row's fate, the designs to create, and the totals the check screen shows. */
export function planImport(input: PlanInput): ImportPlan {
  const { sheet, mapping, words } = input;
  const col = (field: FieldKey) => columnOf(mapping, field);
  const cell = (row: SheetRow, field: FieldKey) => {
    const index = col(field);

    return index < 0 ? '' : (row.cells[index] ?? '').trim();
  };

  const takenCodes = new Set(input.designs.map((design) => design.code.toUpperCase()));
  const active = input.designs.filter((design) => design.isActive !== false);
  const byCode = new Map(active.map((design) => [design.code.toUpperCase(), design]));
  const byName = new Map(
    active.map((design) => [`${design.categoryId}|${wordKey(design.name)}`, design]),
  );
  const newDesigns = new Map<string, NewDesign>();
  const newValues = new Map<string, NewValue>();
  const seenContent = new Map<string, number>();
  const seenSerials = new Set<string>();
  const seenReferences = new Set<string>();

  const chosen = (field: string, word: string): WordChoice | undefined =>
    words[field]?.[wordKey(word)];

  const rows = sheet.rows.map((row): PlannedRow => {
    const reasons: string[] = [];
    let out = false;
    let warn = false;
    const refuse = (reason: string) => {
      out = true;
      reasons.push(reason);
    };
    const caution = (reason: string) => {
      warn = true;
      reasons.push(reason);
    };

    const designName = cell(row, 'designName');
    const designCode = cell(row, 'designCode').toUpperCase();

    // The category: the row's own word, matched or chosen.
    let categoryId: number | null = null;
    const categoryWord = cell(row, 'category');

    if (categoryWord) {
      const choice = chosen('category', categoryWord);
      const matched =
        choice?.kind === 'value' ? choice.id : autoMatch(categoryWord, input.categories);

      if (choice?.kind === 'skip') {
        refuse(`Category “${categoryWord}” is left out`);
      } else if (matched !== null) {
        categoryId = matched;
      }
    } else if (input.defaultCategoryId != null) {
      categoryId = input.defaultCategoryId;
    }

    // The design: by code, else by name in the category, else a new one.
    let design: PlannedRow['design'] = null;
    const existing =
      (designCode ? byCode.get(designCode) : undefined) ??
      (categoryId !== null && designName
        ? byName.get(`${categoryId}|${wordKey(designName)}`)
        : undefined);

    const retired =
      designCode && !existing
        ? input.designs.find(
            (known) => known.isActive === false && known.code.toUpperCase() === designCode,
          )
        : undefined;

    if (existing) {
      design = { id: existing.id };
      categoryId = existing.categoryId;
    } else if (retired) {
      refuse(`Design ${retired.code} is retired — bring it back on its page first`);
    } else if (!designName && !designCode) {
      refuse('No design — the row has neither a name nor a code');
    } else if (categoryId === null) {
      // A category the person chose to leave out has said why already.
      if (!out) {
        refuse(
          categoryWord
            ? `Category “${categoryWord}” is not matched`
            : 'A new design needs a category',
        );
      }
    } else {
      const name = designName || designCode;
      const key = `${categoryId}|${wordKey(name)}`;
      let planned = newDesigns.get(key);

      if (!planned) {
        const code =
          designCode && !takenCodes.has(designCode)
            ? designCode
            : suggestShortCode(name, takenCodes);

        if (code === null) {
          refuse(`No code can be made from “${name}” — give it a design code`);
        } else {
          takenCodes.add(code);
          planned = { key, name, code, categoryId, picture: null, salePrice: null };
          newDesigns.set(key, planned);
        }
      }

      if (planned) {
        design = { key: planned.key };
      }
    }

    // The look: every axis the category carries, from its column and its words.
    const axisValues: Record<number, number> = {};
    const lookParts: string[] = [];
    const axes = categoryId === null ? undefined : input.axesByCategory[categoryId];

    if (categoryId !== null && axes === undefined) {
      refuse('Reading this category’s sizes…');
    }

    for (const axis of axes ?? []) {
      const field = attributeField(axis.attributeCode);
      const word = cell(row, field);

      if (!word) {
        if (axis.isRequired) {
          refuse(
            col(field) < 0
              ? `No column is matched to ${axis.attributeName.toLowerCase()}`
              : `No ${axis.attributeName.toLowerCase()} — this category needs one`,
          );
        }

        continue;
      }

      const choice = chosen(field, word);
      const valueId =
        choice?.kind === 'value'
          ? choice.id
          : autoMatch(
              word,
              axis.values.map((value) => ({
                id: value.valueId,
                name: value.name,
                code: value.code,
              })),
            );

      if (choice?.kind === 'skip') {
        refuse(`${axis.attributeName} “${word}” is left out`);
      } else if (choice?.kind === 'new') {
        const key = `${field}|${wordKey(word)}`;
        let added = newValues.get(key);

        if (!added) {
          added = {
            attributeId: axis.attributeId,
            attributeCode: axis.attributeCode,
            attributeName: axis.attributeName,
            word,
            key: wordKey(word),
            placeholderId: -(newValues.size + 1),
          };
          newValues.set(key, added);
        }

        axisValues[axis.attributeId] = added.placeholderId;
        lookParts.push(`${axis.inGarmentCode ? `${axis.attributeName} ` : ''}${word} (new)`);
      } else if (valueId === null) {
        refuse(`${axis.attributeName} “${word}” is not matched`);
      } else {
        axisValues[axis.attributeId] = valueId;
        lookParts.push(
          `${axis.inGarmentCode ? `${axis.attributeName} ` : ''}${axis.values.find((value) => value.valueId === valueId)?.name ?? word}`,
        );
      }
    }

    // How many: own tags decide it when given, then reference numbers; otherwise the quantity
    // column. "1-5" in a reference cell is five garments, RN-1 to RN-5.
    const serials = serialsOf(cell(row, 'serial'));
    const referenceCell = referencesOf(cell(row, 'reference'));
    const references = referenceCell
      .map(normalizeReference)
      .filter((number): number is string => number !== null);
    const quantityCell = cell(row, 'quantity');
    const counted = serials.length > 0 ? serials.length : references.length;
    let quantity = counted;

    if (counted === 0) {
      const typed =
        quantityCell === '' ? (col('quantity') < 0 ? Number.NaN : 1) : Number(quantityCell);

      if (!Number.isInteger(typed) || typed < 1) {
        refuse(
          quantityCell === '' || Number.isNaN(typed)
            ? 'How many is missing'
            : `How many is ${quantityCell}`,
        );
      } else if (typed > maxPerSize) {
        refuse(`How many is ${typed} — at most ${maxPerSize} on one row`);
      } else {
        quantity = typed;
      }
    } else if (quantityCell !== '' && Number(quantityCell) !== counted) {
      refuse(
        `${counted} ${serials.length > 0 ? 'tags' : 'reference numbers'} given but how many says ${quantityCell}`,
      );
    } else if (counted > maxPerSize) {
      refuse(`${counted} garments on one row — at most ${maxPerSize}`);
    }

    if (serials.length > 0 && references.length > 0 && references.length !== serials.length) {
      refuse(`${serials.length} tags but ${references.length} reference numbers`);
    }

    for (const reference of references) {
      if (!isValidReference(reference)) {
        refuse(`Reference number ${reference} can hold letters, digits, - and / only`);
      } else if (input.takenReferences?.has(reference)) {
        refuse(`RN-${reference} is already on another garment`);
      } else if (seenReferences.has(reference)) {
        refuse(`RN-${reference} is on two rows of this sheet`);
      }

      seenReferences.add(reference);
    }

    for (const serial of serials) {
      if (input.takenSerials.has(serial)) {
        refuse(`Tag ${serial} is already on another garment`);
      } else if (seenSerials.has(serial)) {
        refuse(`Tag ${serial} is on two rows of this sheet`);
      }

      seenSerials.add(serial);
    }

    // The money: a cell that is not a number warns and books in without it.
    const money = (field: FieldKey, what: string): number | null => {
      const value = moneyOf(cell(row, field));

      if (value === null) {
        return null;
      }

      if (Number.isNaN(value) || value < 0) {
        caution(`${what} “${cell(row, field)}” is not an amount — booked in without it`);

        return null;
      }

      return value;
    };

    const cost = money('cost', 'Cost');
    const rentForEvent = money('rentForEvent', 'Rent');
    const extraDayRent = money('extraDayRent', 'Extra day');

    if (extraDayRent !== null && rentForEvent === null) {
      refuse('An extra day’s rent needs the rent for the event');
    }

    // A sale price is a design's, so it reaches only a design this import creates.
    const salePrice = money('salePrice', 'Sale price');

    if (salePrice !== null && design && 'key' in design) {
      const planned = newDesigns.get(design.key);

      if (planned && planned.salePrice === null) {
        planned.salePrice = salePrice;
      }
    } else if (salePrice !== null && design) {
      caution('Sale price is left as the design’s own — the sheet’s is used only for new designs');
    }

    if (col('cost') >= 0 && cost === null && !reasons.some((reason) => reason.startsWith('Cost'))) {
      caution('No cost — booked in without one');
    }

    // The condition: a word the shop uses, or chosen; new when the sheet does not say.
    const conditionWord = cell(row, 'condition');
    let condition: UnitCondition = 'New';

    if (conditionWord) {
      const known = conditionOf(conditionWord);

      if (known) {
        condition = known;
      } else {
        caution(`Condition “${conditionWord}” is not known — booked in as New`);
      }
    }

    // The photo: a picture on the row, or a file by the name in the photo column.
    let picture = row.picture;
    const photoName = cell(row, 'photo');

    if (!picture && /^https?:\/\/\S+$/i.test(photoName)) {
      // A web link is kept as the new design's photo link, the way a design can already hold one.
      picture = `url:${photoName}`;
    } else if (!picture && photoName && input.hasPhotoFile) {
      if (input.hasPhotoFile(photoName)) {
        picture = `file:${photoName}`;
      } else {
        caution(`Photo “${photoName}” was not among the files chosen — imported without one`);
      }
    }

    // A new design takes the first photo among its rows; a known design keeps its own.
    if (picture && design && 'key' in design) {
      const planned = newDesigns.get(design.key);

      if (planned && planned.picture === null) {
        planned.picture = picture;
      }
    }

    const content = [
      designCode || wordKey(designName),
      categoryId ?? '',
      Object.entries(axisValues).sort().join(','),
      quantity,
      serials.join(','),
      cost ?? '',
      rentForEvent ?? '',
      // Only when there are any, so a row without reference numbers keeps the fingerprint it was
      // imported under before they existed — and is still recognised as done.
      ...(references.length > 0 ? [`rn:${references.join(',')}`] : []),
    ].join('|');
    const occurrence = seenContent.get(content) ?? 0;
    seenContent.set(content, occurrence + 1);
    const fingerprint = fingerprintOf(content, occurrence);
    // A row that came in before is done, whatever else it now says — its own tags are "taken" by
    // the very garments it brought in, and that must not read as a problem.
    const done = input.imported.has(fingerprint);

    return {
      number: row.number,
      status: done ? 'done' : out ? 'out' : warn ? 'warning' : 'ready',
      reasons: done ? ['Already imported — skipped'] : reasons,
      designName: designName || designCode,
      design,
      categoryId,
      axisValues,
      look: lookParts.join(' · '),
      quantity,
      serials,
      references,
      cost,
      rentForEvent,
      extraDayRent,
      condition,
      picture,
      notes: cell(row, 'notes') || null,
      fingerprint,
    };
  });

  // A new design that only left-out or done rows point at is not created.
  const wanted = new Set(
    rows
      .filter(
        (row) =>
          (row.status === 'ready' || row.status === 'warning') && row.design && 'key' in row.design,
      )
      .map((row) => (row.design as { key: string }).key),
  );
  const importing = rows.filter((row) => row.status === 'ready' || row.status === 'warning');
  const creating = [...newDesigns.values()].filter((design) => wanted.has(design.key));
  const placeholders = new Set(
    importing.flatMap((row) => Object.values(row.axisValues).filter((id) => id < 0)),
  );

  return {
    rows,
    newDesigns: creating,
    newValues: [...newValues.values()].filter((value) => placeholders.has(value.placeholderId)),
    summary: {
      read: rows.length,
      ready: rows.filter((row) => row.status === 'ready').length,
      warning: rows.filter((row) => row.status === 'warning').length,
      out: rows.filter((row) => row.status === 'out').length,
      done: rows.filter((row) => row.status === 'done').length,
      garments: importing.reduce((sum, row) => sum + row.quantity, 0),
      ownTags: importing.reduce((sum, row) => sum + row.serials.length, 0),
      references: importing.reduce((sum, row) => sum + row.references.length, 0),
      pictures: creating.filter((design) => design.picture !== null).length,
      cost: importing.reduce((sum, row) => sum + (row.cost ?? 0) * row.quantity, 0),
    },
  };
}

/**
 * The choices once the new values exist: each "add as new" becomes the value it created, so
 * planning again books those rows in with real ids — and their fingerprints are the ones a later
 * run of the same sheet will compute.
 */
export function withAddedValues(
  words: WordMap,
  added: readonly NewValue[],
  createdIds: Record<number, number>,
): WordMap {
  const next: WordMap = Object.fromEntries(
    Object.entries(words).map(([field, choices]) => [field, { ...choices }]),
  );

  for (const value of added) {
    const id = createdIds[value.placeholderId];

    if (id !== undefined) {
      const field = attributeField(value.attributeCode);
      next[field] = { ...next[field], [value.key]: { kind: 'value', id } };
    }
  }

  return next;
}

/** A word the sheet uses that nothing known names, for the person to decide. */
export interface WordToMatch {
  /** `category` or an `attr:` field — the key in a `WordMap`. */
  field: string;
  /** "Category", "Size" — what the word is a word for. */
  label: string;
  word: string;
  key: string;
  rows: number;
  options: Choosable[];
  /** Whether "add as new" is offered: values yes; categories are made on the Categories page. */
  canAdd: boolean;
}

/**
 * The category and attribute words that match nothing known, most used first per field. A word
 * the person has already decided stays listed (so the choice can be changed); a word that matches
 * on its own is not shown.
 */
export function wordsToMatch(input: {
  sheet: Sheet;
  mapping: readonly FieldKey[];
  words: WordMap;
  categories: readonly Choosable[];
  axesByCategory: Record<number, ItemVariantAxisOptions[] | undefined>;
}): WordToMatch[] {
  const result: WordToMatch[] = [];
  const categoryColumn = columnOf(input.mapping, 'category');

  if (categoryColumn >= 0) {
    for (const { word, key, rows } of distinctWords(input.sheet, categoryColumn)) {
      if (input.words.category?.[key] || autoMatch(word, input.categories) === null) {
        result.push({
          field: 'category',
          label: 'Category',
          word,
          key,
          rows,
          options: [...input.categories],
          canAdd: false,
        });
      }
    }
  }

  // Each attribute's values, gathered from every category that carries it.
  const valuesByCode = new Map<string, { name: string; options: Map<number, Choosable> }>();

  for (const axes of Object.values(input.axesByCategory)) {
    for (const axis of axes ?? []) {
      const entry = valuesByCode.get(axis.attributeCode) ?? {
        name: axis.attributeName,
        options: new Map<number, Choosable>(),
      };

      for (const value of axis.values) {
        entry.options.set(value.valueId, { id: value.valueId, name: value.name, code: value.code });
      }

      valuesByCode.set(axis.attributeCode, entry);
    }
  }

  input.mapping.forEach((field, column) => {
    const code = attributeCodeOf(field);
    const known = code === null ? undefined : valuesByCode.get(code);

    if (!known) {
      return;
    }

    const options = [...known.options.values()];

    for (const { word, key, rows } of distinctWords(input.sheet, column)) {
      if (input.words[field]?.[key] || autoMatch(word, options) === null) {
        result.push({ field, label: known.name, word, key, rows, options, canAdd: true });
      }
    }
  });

  return result;
}

/* ----------------------------------------------------------- the batches */

/** One request's worth of rows: at most this many garments, so a dropped connection costs a batch. */
export const garmentsPerBatch = 100;

export interface ImportBatch {
  draft: IntakeDraft;
  rows: { number: number; fingerprint: string }[];
}

/**
 * The rows being imported as intake drafts of about a hundred garments each, in sheet order — one
 * intake line per row, its design resolved now that new designs have ids. Each batch goes up whole
 * or not at all, and carries its rows' fingerprints so the server can record them.
 */
export function importBatches(
  plan: ImportPlan,
  newDesignIds: Record<string, number>,
  delivery: { purchasedOn: string; supplierVendorId: number | null; note: string },
): ImportBatch[] {
  const batches: ImportBatch[] = [];
  let lines: IntakeLine[] = [];
  let rows: ImportBatch['rows'] = [];
  let garments = 0;

  const flush = () => {
    if (lines.length > 0) {
      batches.push({
        draft: {
          purchasedOn: delivery.purchasedOn,
          supplierVendorId: delivery.supplierVendorId,
          supplierReference: delivery.note,
          notes: '',
          lines,
        },
        rows,
      });
    }

    lines = [];
    rows = [];
    garments = 0;
  };

  for (const row of plan.rows) {
    if ((row.status !== 'ready' && row.status !== 'warning') || !row.design) {
      continue;
    }

    const itemId = 'id' in row.design ? row.design.id : newDesignIds[row.design.key];

    if (itemId === undefined) {
      continue;
    }

    if (Object.values(row.axisValues).some((id) => id < 0)) {
      // A placeholder reaching the server would be refused anyway; this names the real mistake.
      throw new Error('New values must be added, and the sheet planned again, before importing');
    }

    if (garments + row.quantity > garmentsPerBatch) {
      flush();
    }

    lines.push({
      ...emptyLine(),
      itemId,
      axisValues: row.axisValues,
      quantity: row.quantity,
      condition: row.condition,
      purchaseCost: row.cost,
      rentForEvent: row.rentForEvent,
      extraDayRent: row.extraDayRent,
      serialCodes: row.serials.join('\n'),
      ownSerials: row.serials.length > 0,
      referenceNumbers: row.references,
      notes: row.notes,
    });
    rows.push({ number: row.number, fingerprint: row.fingerprint });
    garments += row.quantity;
  }

  flush();

  return batches;
}

/* -------------------------------------------------------------- the layout */

/** The headings as one key — the next sheet with the same headings is matched the same way. */
export function layoutKey(headers: readonly string[]): string {
  return headers.map(headingKey).join('|');
}
